"use client";

import Image from "next/image";
import { useEffect } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  cartOpenAtom,
  cartItemsAtom,
  cartTotalPriceAtom,
  cartLoadingAtom,
} from "@/store";
import { useAnalytics } from "@/components/providers/analytics-provider";

export function CartDrawer() {
  const [cartOpen, setCartOpen] = useAtom(cartOpenAtom);
  const [cartItems] = useAtom(cartItemsAtom);
  const [totalPrice] = useAtom(cartTotalPriceAtom);
  const [loading] = useAtom(cartLoadingAtom);

  const { trackTyped } = useAnalytics();

  useEffect(() => {
    if (cartOpen) {
      trackTyped("ecommerce_cart_view", {
        item_count: cartItems.length,
        total_value:
          typeof totalPrice?.amount === "string"
            ? parseFloat(totalPrice.amount)
            : Number(totalPrice?.amount) || 0,
      });
    }
  }, [cartOpen, cartItems.length, totalPrice.amount, trackTyped]);

  const handleCheckout = () => {
    trackTyped("ecommerce_checkout_start", {
      item_count: cartItems.length,
      total_value: Number(
        cartItems
          .reduce((a, c) => a + parseFloat(c.price.amount) * c.quantity, 0)
          .toFixed(2)
      ),
    });
  };

  const removeItem = (itemId: string) => {
    trackTyped("ecommerce_remove_from_cart", { product_id: itemId });
  };

  const updateQuantity = (itemId: string, newQuantity: number) => {
    trackTyped("ecommerce_update_quantity", {
      product_id: itemId,
      quantity: newQuantity,
    });
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Shopping Cart
            {cartItems.length > 0 && (
              <Badge variant="secondary">{cartItems.length}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full">
          {cartItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <h3 className="font-serif text-lg font-medium text-stone-900 mb-2 tracking-tight">
                  Your cart is empty
                </h3>
                <p className="text-stone-500 mb-4">
                  Add some Concretta microcement to get started.
                </p>
                <Button onClick={() => setCartOpen(false)}>
                  Continue Shopping
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto py-6">
                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="flex items-center space-x-4 p-4 border border-stone-200 rounded-lg"
                    >
                      {item.image && (
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-stone-200">
                          <Image
                            src={item.image.url}
                            alt={item.image.altText || item.productTitle}
                            width={64}
                            height={64}
                            className="h-full w-full object-cover object-center"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-stone-900 truncate">
                          {item.productTitle}
                        </h4>
                        {item.variantTitle !== "Default Title" && (
                          <p className="text-sm text-stone-500">
                            {item.variantTitle}
                          </p>
                        )}
                        <p className="text-sm font-medium text-stone-900">
                          ${parseFloat(item.price.amount).toFixed(2)}{" "}
                          {item.price.currencyCode}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              Math.max(0, item.quantity - 1)
                            )
                          }
                          disabled={loading}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm font-medium w-8 text-center">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                          disabled={loading}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                        disabled={loading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </div>
              <Separator />
              <div className="py-6 space-y-4">
                <div className="flex justify-between text-base font-medium text-stone-900">
                  <p>Total</p>
                  <p>
                    ${parseFloat(totalPrice.amount).toFixed(2)}{" "}
                    {totalPrice.currencyCode}
                  </p>
                </div>
                <p className="text-sm text-stone-500">
                  Shipping and taxes calculated at checkout.
                </p>
                <Button
                  onClick={handleCheckout}
                  className="w-full"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Checkout"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCartOpen(false)}
                  className="w-full"
                >
                  Continue Shopping
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
